import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", path: "/" },
  { label: "影视分类", path: "/category" },
  { label: "搜索", path: "/search" },
  { label: "关于我们", path: "/about" },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    setMenuOpen(false);
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path) || (path === "/category" && location.pathname.startsWith("/detail"));
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-primary-950 border-b border-primary-800/70">
      <div className="flex h-16 md:h-[72px] w-full items-center justify-between gap-4 px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2.5 md:gap-3 min-w-0 group">
          <span className="flex h-9 w-9 md:h-10 md:w-10 shrink-0 items-center justify-center rounded-md bg-accent-500 font-label text-sm md:text-base font-bold tracking-tight text-primary-950">
            HD
          </span>
          <span className="flex flex-col leading-tight min-w-0">
            <span className="truncate text-base md:text-lg font-bold tracking-tight text-background-50">
              诱人的女员工hd
            </span>
            <span className="hidden md:block text-[10px] tracking-[0.35em] text-primary-300 uppercase font-label">
              Film &amp; Series
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 lg:gap-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`relative whitespace-nowrap rounded-md px-3 lg:px-4 py-2 text-sm transition-colors cursor-pointer ${
                isActive(item.path)
                  ? "text-accent-400 font-medium"
                  : "text-primary-100 hover:text-background-50 hover:bg-primary-900/70"
              }`}
            >
              {item.label}
              {isActive(item.path) && (
                <span className="absolute inset-x-3 -bottom-[1px] h-0.5 rounded-full bg-accent-500" />
              )}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <form onSubmit={handleSearch} className="hidden lg:flex items-center gap-2 rounded-md bg-primary-900/80 border border-primary-700/60 px-3 h-10 w-64">
            <i className="ri-search-line text-primary-300 text-base w-4 h-4 flex items-center justify-center" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视作品或演员"
              aria-label="搜索影视作品"
              className="w-full bg-transparent text-sm text-background-50 placeholder:text-primary-400 outline-none"
            />
            <button
              type="submit"
              className="shrink-0 rounded-sm bg-accent-500 px-2 py-1 text-[11px] font-medium text-primary-950 cursor-pointer hover:bg-accent-400 transition-colors whitespace-nowrap"
            >
              搜索
            </button>
          </form>

          <Link
            to="/search"
            aria-label="搜索"
            className="lg:hidden flex h-9 w-9 items-center justify-center rounded-md text-primary-100 hover:bg-primary-900 cursor-pointer transition-colors"
          >
            <i className="ri-search-line text-lg w-5 h-5 flex items-center justify-center" />
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="打开导航菜单"
            aria-expanded={menuOpen}
            className="md:hidden flex h-9 w-9 items-center justify-center rounded-md text-primary-100 hover:bg-primary-900 cursor-pointer transition-colors"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl w-6 h-6 flex items-center justify-center`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-primary-800/70 bg-primary-950 px-4 pb-5 pt-3">
          <form onSubmit={handleSearch} className="flex items-center gap-2 rounded-md bg-primary-900 border border-primary-700/60 px-3 h-11 mb-3">
            <i className="ri-search-line text-primary-300 w-4 h-4 flex items-center justify-center" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视作品或演员"
              aria-label="移动端搜索影视作品"
              className="w-full bg-transparent text-sm text-background-50 placeholder:text-primary-400 outline-none"
            />
          </form>
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center justify-between rounded-md px-3 py-3 text-sm cursor-pointer ${
                  isActive(item.path)
                    ? "bg-primary-900 text-accent-400 font-medium"
                    : "text-primary-100 hover:bg-primary-900"
                }`}
              >
                {item.label}
                <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-primary-400" />
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}